import React, { useCallback, useEffect, useState } from "react";
import { Loader2, MessageSquareQuote, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { devWarn } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

/**
 * Optional, opt-in testimonial submission.
 *
 * CONSENT DESIGN (the whole point of this component)
 *  - Entirely optional, and nothing is submitted by default.
 *  - The publish consent box is unticked and the Share button stays disabled
 *    until it is ticked, so consent can never be implied from silence.
 *  - Attribution defaults to "Anonymous" - the safest option - and the member
 *    picks first name / initials / anonymous themselves.
 *  - Showing a photo is a separate, opt-in choice that defaults to off.
 *  - The form tells the member plainly that an admin reviews it first, that
 *    nothing is published automatically, and that they can withdraw it.
 *  - The helper text warns against including recovery details they would not
 *    want public, because publishing recovery history without specific consent
 *    is off limits.
 */
const STATUS_COPY = {
  pending: "Waiting for review - nothing is public yet.",
  approved: "Published on the site with the name option you chose.",
  rejected: "Not published. Nothing of yours is public.",
};

const NAME_OPTIONS = [
  { value: "anonymous", label: "Anonymous member", hint: "No name shown at all" },
  { value: "initials", label: "My initials", hint: "For example: T. A." },
  { value: "first_name", label: "My display name", hint: "The name on your profile" },
];

export default function TestimonialSection() {
  const [mine, setMine] = useState([]);
  const [quote, setQuote] = useState("");
  const [namePref, setNamePref] = useState("anonymous");
  const [showPhoto, setShowPhoto] = useState(false);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [consentWording, setConsentWording] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/testimonials/mine");
      setMine(data?.testimonials || []);
      setConsentWording(data?.consent_wording || "");
    } catch (err) {
      devWarn("TestimonialSection", err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!consent || quote.trim().length < 20) return;
    setBusy(true);
    try {
      await api.post("/testimonials", {
        quote: quote.trim(),
        display_preference: namePref,
        show_photo: showPhoto,
        consent_publish: true,
      });
      toast.success("Thank you - an admin will review it before anything appears publicly.");
      setQuote("");
      setConsent(false);
      setShowPhoto(false);
      setNamePref("anonymous");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async (id) => {
    setBusy(true);
    try {
      await api.delete(`/testimonials/${id}`);
      toast.success("Removed.");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="testimonial-section" className="sm-card p-6 bg-white">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 shrink-0 rounded-xl bg-[#D7EEF0] text-[#1F6F78] flex items-center justify-center">
          <MessageSquareQuote className="w-5 h-5" aria-hidden="true" />
        </span>
        <div>
          <h2 className="font-display text-lg font-bold text-[#1F2A2E]">
            Share your experience (optional)
          </h2>
          <p className="mt-1 text-sm text-[#4B5A60] leading-6">
            If Sponsor Me has been useful, you can send a short note we may publish on the site.
            This is completely optional, nothing is published without your permission, an admin
            reviews it first, and you can ask for it to be removed at any time.
          </p>
        </div>
      </div>

      {mine.length > 0 && (
        <ul data-testid="testimonial-mine" className="mt-5 space-y-3">
          {mine.map((t) => (
            <li
              key={t.id}
              data-testid={`testimonial-mine-${t.id}`}
              className="rounded-xl border border-[#E3DED4] bg-[#FBF9F5] p-4"
            >
              <p className="text-sm text-[#1F2A2E] leading-6">“{t.quote}”</p>
              <p className="mt-2 text-xs text-[#4B5A60]">
                Shown as <span className="font-semibold">{t.preview_label}</span> ·{" "}
                {STATUS_COPY[t.status] || t.status}
              </p>
              <Button
                type="button"
                variant="ghost"
                disabled={busy}
                onClick={() => withdraw(t.id)}
                data-testid={`testimonial-withdraw-${t.id}`}
                className="mt-2 h-9 px-3 text-[#B42318] hover:bg-[#FBE6E4]"
              >
                <Trash2 className="w-4 h-4 mr-1.5" aria-hidden="true" /> Remove it
              </Button>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={submit} className="mt-5 space-y-5">
        <div>
          <Label htmlFor="testimonial-quote" className="text-sm font-semibold text-[#1F2A2E]">
            In your own words
          </Label>
          <Textarea
            id="testimonial-quote"
            value={quote}
            onChange={(e) => setQuote(e.target.value)}
            maxLength={600}
            rows={4}
            placeholder="What was it like using Sponsor Me?"
            data-testid="testimonial-quote-input"
            className="mt-2 bg-white border-[#E3DED4]"
          />
          <p className="mt-1.5 text-xs text-[#7A868B]">
            Please don't include anything you would not want public - no recovery history, health
            details, other people's names, or contact details. {quote.length}/600
          </p>
        </div>

        <fieldset>
          <legend className="text-sm font-semibold text-[#1F2A2E]">
            How should we credit you?
          </legend>
          <RadioGroup
            value={namePref}
            onValueChange={setNamePref}
            className="mt-2 grid gap-2 sm:grid-cols-3"
          >
            {NAME_OPTIONS.map((o) => (
              <label
                key={o.value}
                className="flex items-start gap-2.5 rounded-xl border border-[#E3DED4] bg-[#FBF9F5] px-3.5 py-3 cursor-pointer hover:bg-[#F2EFE8] has-[:checked]:border-[#1F6F78] has-[:checked]:bg-[#D7EEF0]"
              >
                <RadioGroupItem
                  value={o.value}
                  data-testid={`testimonial-name-${o.value}`}
                  className="mt-0.5"
                />
                <span className="text-sm text-[#1F2A2E]">
                  {o.label}
                  <span className="block text-xs text-[#7A868B]">{o.hint}</span>
                </span>
              </label>
            ))}
          </RadioGroup>
        </fieldset>

        <label className="flex items-start gap-3">
          <Checkbox
            checked={showPhoto}
            onCheckedChange={(v) => setShowPhoto(Boolean(v))}
            data-testid="testimonial-photo-consent"
            className="mt-0.5"
          />
          <span className="text-sm text-[#1F2A2E]">
            Show my profile photo next to my words
            <span className="block text-xs text-[#7A868B] mt-0.5">
              Off by default. Only the photo already on your profile can be used.
            </span>
          </span>
        </label>

        <label className="flex items-start gap-3 rounded-xl border border-[#E3DED4] bg-[#FBF9F5] p-4">
          <Checkbox
            checked={consent}
            onCheckedChange={(v) => setConsent(Boolean(v))}
            data-testid="testimonial-publish-consent"
            className="mt-0.5"
          />
          <span className="text-sm text-[#1F2A2E] leading-6">
            {consentWording ||
              "I agree that Sponsor Me may publish this quote publicly, using the name option and photo option I selected. I understand an admin reviews it first, and that I can ask for it to be removed at any time."}
          </span>
        </label>

        <Button
          type="submit"
          disabled={busy || !consent || quote.trim().length < 20}
          data-testid="testimonial-submit"
          className="h-11 px-6 bg-[#1F6F78] hover:bg-[#185B62] text-white disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send for review"}
        </Button>
      </form>
    </section>
  );
}
